import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { isoDateSchema } from "@common/helpers/date";
import { TransactionBaseDto } from "../dto/transactions/transaction-base.dto";
import { ErrorMessageSchema } from "../schemas/error.schema";
import { updateManualTransactionBodySchema } from "../schemas/domains/transactions/manual-transaction.schema";

const c = initContract();

export const createManualTransactionsBodySchema = z
    .object({
        financialAccountId: z.number().optional(),
        createCashAccount: z.boolean().optional(),
        entityId: z.number(),
        transactions: z.array(
            z
                .object({
                    categoryId: z.string().uuid().optional(),
                    accountId: z.string().uuid().optional(),
                    amount: z.number(),
                    date: z.coerce.date().optional(),
                    dateOnly: isoDateSchema.optional(),
                    memo: z.string().optional().nullable(),
                    classIds: z.array(z.string().uuid()).optional(),
                    counterpartyId: z.string().uuid().optional(),
                    bankDescription: z.string().min(1).optional(),
                })
                .superRefine(
                    ({ categoryId, accountId, date, dateOnly }, ctx) => {
                        if (categoryId != null && accountId != null) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message:
                                    "Provide only one of categoryId or accountId",
                                path: ["categoryId"],
                            });
                        }
                        if ((date == null) === (dateOnly == null)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message:
                                    "Provide exactly one of date or dateOnly",
                                path: ["dateOnly"],
                            });
                        }
                    },
                ),
        ),
    })
    .refine(
        (data) => {
            const { financialAccountId, createCashAccount } = data;

            if (
                financialAccountId &&
                financialAccountId > 0 &&
                !createCashAccount
            ) {
                return true;
            }

            if (createCashAccount && !financialAccountId) {
                return true;
            }

            return false;
        },
        {
            message:
                "Exactly one of financialAccountId or createCashAccount must be defined.",
            path: ["financialAccountId"],
        },
    );

export interface ManualTransactionsCreationResponse {
    transactions: TransactionBaseDto[];
}

export const manualTransactionContract = c.router(
    {
        createManualTransactions: {
            method: "POST",
            path: "",
            body: createManualTransactionsBodySchema,
            responses: {
                200: c.type<ManualTransactionsCreationResponse>(),
                400: c.type<{ message: string }>(),
            },
        },
        updateManualTransaction: {
            method: "PATCH",
            path: "/:entityId/:transactionId",
            pathParams: z.object({
                entityId: z.coerce.number(),
                transactionId: z.coerce.number(),
            }),
            body: updateManualTransactionBodySchema,
            responses: {
                200: c.type<TransactionBaseDto>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/manual-transaction",
    },
);
